<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { DeleteDialog } from '@haskoning/vue-component-library'

import { useCalculationStore } from '@/stores/calculation'
import RescheduleCalculationDialog from '@/components/calculation/RescheduleCalculationDialog.vue'
import StopCalculationDialog from '@/components/calculation/StopCalculationDialog.vue'
import type { CalculationReschedulePayload } from '@/stores/type/calculation.type'
import { useCalculationPermissions } from '@/composables/useCalculationPermissions'

const props = defineProps<{
  spendingCapExceeded?: boolean
  spendingLoaded?: boolean
  spendingLoading?: boolean
}>()

const emit = defineEmits<{
  rescheduled: []
  stopped: []
}>()

const router = useRouter()
const calculationStore = useCalculationStore()
const {
  currentItem,
  zipFileDownloadLoading,
  rescheduleLoading,
  stopLoading,
  deleteDialog,
  deleteItem,
  deleteLoading
} = storeToRefs(calculationStore)
const { canAddCalculation, canStopCalculation, canDeleteCalculation } = useCalculationPermissions()
const rescheduleDialog = ref(false)
const stopDialog = ref(false)

const isZipFileDownloadLoading = computed(() => {
  return currentItem.value?.id === zipFileDownloadLoading.value
})

const isRescheduling = computed(() => {
  return currentItem.value?.id === rescheduleLoading.value
})

const isStopping = computed(() => {
  return currentItem.value?.id === stopLoading.value
})

const isRunning = computed(() => currentItem.value?.status === 'RUNNING')

const canReschedule = computed(() =>
  ['ERROR', 'TIMEOUT', 'CANCELLED'].includes(String(currentItem.value?.status ?? ''))
)

const rescheduleDisabledReason = computed(() => {
  if (!canReschedule.value) return ''
  if (props.spendingLoading || !props.spendingLoaded) return 'Checking project spending caps.'
  if (props.spendingCapExceeded) {
    return 'Project spending caps are exceeded. Increase the cap before rescheduling.'
  }
  return ''
})

const rescheduleDisabled = computed(() => {
  return Boolean(rescheduleDisabledReason.value) || isRescheduling.value
})

const formatDate = (dateString?: string) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const navigateToProject = () => {
  if (!currentItem.value) return
  router.push({ name: 'project-detail', params: { id: currentItem.value.project } })
}

const downloadZipFile = () => {
  if (!currentItem.value || isZipFileDownloadLoading.value) return
  calculationStore.downloadZipFile(currentItem.value)
}

const rescheduleCalculation = async (payload: CalculationReschedulePayload) => {
  if (!currentItem.value || rescheduleDisabled.value) return
  const calculation = await calculationStore.rescheduleCalculation(currentItem.value, payload)
  if (!calculation) return
  rescheduleDialog.value = false
  emit('rescheduled')
}

const stopCalculation = async () => {
  if (!currentItem.value || !isRunning.value || isStopping.value) return
  const calculation = await calculationStore.stopCalculation(currentItem.value)
  if (!calculation) return
  stopDialog.value = false
  emit('stopped')
}

const openDeleteCalculationDialog = () => {
  if (!currentItem.value) return
  calculationStore.setDeleteItem(currentItem.value)
  calculationStore.openDeleteDialog()
}

const deleteCalculation = async () => {
  const calculation = deleteItem.value
  if (!calculation) return
  await calculationStore.delete(calculation)
  await router.push({ name: 'project-detail', params: { id: calculation.project } })
}
</script>

<template>
  <v-card v-if="currentItem" class="mx-2 py-4" elevation="0" flat>
    <v-row no-gutters class="align-center">
      <v-col class="text-center">
        <v-card-title class="header-font d-flex align-center justify-center">
          <span> Results for {{ currentItem.name }} - {{ formatDate(currentItem.created) }} </span>
        </v-card-title>
        <v-card-subtitle class="description-font mb-6">
          {{ currentItem.note }}
        </v-card-subtitle>
      </v-col>
    </v-row>
  </v-card>

  <v-card
    class="d-flex flex-wrap align-center justify-center mx-2 py-4"
    color="#F6F6F6"
    elevation="0"
    flat
  >
    <v-card-actions>
      <v-btn
        class="text-none px-4"
        variant="tonal"
        prepend-icon="mdi-arrow-left"
        @click="navigateToProject"
      >
        Back to project
      </v-btn>
      <v-btn
        class="text-none px-4"
        variant="tonal"
        prepend-icon="mdi-download"
        :loading="isZipFileDownloadLoading"
        @click="downloadZipFile"
      >
        Download input file
      </v-btn>
      <v-btn
        v-if="canStopCalculation && isRunning"
        class="text-none px-4"
        color="error"
        variant="tonal"
        prepend-icon="mdi-stop-circle-outline"
        :loading="isStopping"
        :disabled="isStopping"
        @click="stopDialog = true"
      >
        Stop calculation
      </v-btn>
      <v-tooltip
        v-if="canAddCalculation && canReschedule"
        :disabled="!rescheduleDisabledReason"
        :text="rescheduleDisabledReason"
        location="top"
      >
        <template #activator="{ props }">
          <span v-bind="props">
            <v-btn
              class="text-none px-4"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-restart"
              :disabled="rescheduleDisabled"
              :loading="isRescheduling"
              @click="rescheduleDialog = true"
            >
              Reschedule
            </v-btn>
          </span>
        </template>
      </v-tooltip>
      <v-btn
        v-if="canDeleteCalculation"
        class="text-none px-4"
        color="error"
        variant="tonal"
        prepend-icon="mdi-trash-can-outline"
        @click="openDeleteCalculationDialog"
      >
        Delete calculation
      </v-btn>
    </v-card-actions>
  </v-card>

  <RescheduleCalculationDialog
    v-if="currentItem"
    v-model="rescheduleDialog"
    :calculation-name="currentItem.name"
    :project-id="currentItem.project"
    :hardware-profile-id="currentItem.hardware_profile"
    :container-image="currentItem.container_image"
    :vm-max-runtime-seconds="currentItem.vm_max_runtime_seconds"
    :loading="isRescheduling"
    @confirm="rescheduleCalculation"
  />

  <StopCalculationDialog
    v-if="currentItem"
    v-model="stopDialog"
    :calculation-name="currentItem.name"
    :loading="isStopping"
    @confirm="stopCalculation"
  />

  <DeleteDialog
    v-if="deleteItem"
    v-model="deleteDialog"
    :loading="deleteLoading"
    :name="deleteItem?.name"
    :item="deleteItem"
    @set-delete-item="deleteItem = $event"
    @delete="deleteCalculation"
  />
</template>
